
import { useState, useRef, useEffect, useCallback } from 'react';
import { calculateDaysToShow, calculateCellWidth } from './calendarHelpers';

export function useCalendarSize() {
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const [daysToShow, setDaysToShow] = useState(7);
  const [cellWidth, setCellWidth] = useState(150);
  const [lastResize, setLastResize] = useState(Date.now());

  // Function to handle calendar container resizing
  const handleCalendarResize = useCallback(() => {
    if (calendarContainerRef.current) {
      const containerWidth = calendarContainerRef.current.offsetWidth;
      const newDaysToShow = calculateDaysToShow(containerWidth);
      setDaysToShow(newDaysToShow);
      setCellWidth(calculateCellWidth(containerWidth, newDaysToShow));
    }
  }, []);

  // Set up resize handling
  useEffect(() => {
    // Initial size calculation
    handleCalendarResize();

    // Set up ResizeObserver to monitor container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Throttle resize events to avoid excessive renders
      const now = Date.now();
      if (now - lastResize > 100) { // Only process resize every 100ms
        setLastResize(now);
        handleCalendarResize();
      }
    });

    if (calendarContainerRef.current) {
      resizeObserver.observe(calendarContainerRef.current);
    }

    // Also listen to window resize events as a fallback
    window.addEventListener('resize', handleCalendarResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleCalendarResize);
    };
  }, [handleCalendarResize, lastResize]);

  return {
    calendarContainerRef,
    daysToShow,
    cellWidth
  };
}
